/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, max-lines */
'use client';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';

import { AbgTypography, formatMoney, mergeSx } from '@abg/kit-base';

import { usePricingDisplayConfig } from '../../hooks';
import { useSyncAncillariesWithBookingStore } from '../../organisms/Ancillaries/hooks/use-sync-ancillaries-with-booking-store';
import { AncillariesPageType } from '../../shared/constants';
import { useTranslationContext } from '../../shared/context';
import { AncillariesI18nKeys } from '../../shared/types';
import { sanitizeHtml } from '../../shared/utils';
import { AemImage } from '../AemImage';

import { AncillariesBundleItems } from './AncillariesBundle/AncillariesBundleItems';
import { AncillariesBundleConfig } from './AncillariesBundle/types';
import { useIsBundleDisabled } from './hooks/use-is-bundle-disabled';
import { AncillariesBundlesProps } from './types';

export const AsciAncillariesBundles = ({
  styleParts = {},
  bundles,
  selectedBundle,
  onBundleSelected,
  pageType,
}: AncillariesBundlesProps) => {
  useSyncAncillariesWithBookingStore({ pageType });
  const { isBundleDisabled, isBundleIncluded } = useIsBundleDisabled();
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const { showDailyRate } = usePricingDisplayConfig();
  const isSmallMobile = useMediaQuery('(max-width:450px)');
  const [expandedAccordion, setExpandedAccordion] = useState<number | false>(0);

  const priceLabelSuffix = showDailyRate ? `/${t(i18nKeys.day)}` : '';

  const handleAccordionChange =
    (panel: number) => (_event: React.SyntheticEvent, expanded: boolean) => {
      setExpandedAccordion(expanded ? panel : false);
    };

  const renderPrice = (bundle: AncillariesBundleConfig) => {
    const currency = bundle.currencyCode ?? 'USD';
    return (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Box>
          <AbgTypography
            variant="h6"
            sx={{ ...styleParts.price }}
            data-testid="asci-bundle-price"
          >
            +{formatMoney(Number(bundle.price ?? 0), { currency })}
            {priceLabelSuffix}
          </AbgTypography>
        </Box>
        {!!bundle.oldPrice && (
          <AbgTypography
            sx={mergeSx(
              { textDecoration: 'line-through', color: '#736D6D' },
              styleParts.oldPrice,
            )}
            variant="bodySmallRegular"
            data-testid="asci-bundle-old-price"
          >
            {formatMoney(Number(bundle.oldPrice), { currency })}
            {priceLabelSuffix}
          </AbgTypography>
        )}
      </Box>
    );
  };

  const renderSelectButton = (
    bundle: AncillariesBundleConfig,
    disabled: boolean,
    included: boolean,
  ) => {
    if (disabled || included) return null;
    const isSelected = selectedBundle === bundle.code;
    const isProtectionsPage = pageType === AncillariesPageType.Protections;

    // For protections page, show a clickable "Selected" button that can be toggled
    if (isSelected && isProtectionsPage) {
      return (
        <Box
          component="div"
          role="button"
          tabIndex={0}
          aria-label="Unselect"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onBundleSelected(bundle.code);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              onBundleSelected(bundle.code);
            }
          }}
          sx={{
            backgroundColor: (styleParts as any)?.selectButton?.backgroundColor || 'black',
            borderRadius: '100px',
            color: 'white',
            padding: '8px 10px 8px 3px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '36px',
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.75,
            textTransform: 'none',
            transition:
              'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&:hover': {
              backgroundColor: (styleParts as any)?.selectButton?.backgroundColor || '#333',
            },
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px',
            },
          }}
        >
          <CheckIcon sx={{ mr: 1, fontSize: '16px' }} />
          Selected
        </Box>
      );
    }

    return (
      <Box
        component="div"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onBundleSelected(bundle.code);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            onBundleSelected(bundle.code);
          }
        }}
        sx={{
          backgroundColor: (styleParts as any)?.selectButton?.backgroundColor || 'black',
          borderRadius: '100px',
          color: 'white',
          padding: isSelected ? '8px 10px 8px 3px' : '9px 16px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '36px',
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: 1.75,
          textTransform: 'none',
          transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          '&:hover': {
            backgroundColor: (styleParts as any)?.selectButton?.backgroundColor || '#333',
          },
          '&:focus': {
            outline: '2px solid #1976d2',
            outlineOffset: '2px',
          },
        }}
      >
        {isSelected ? (
          <>
            <CheckIcon sx={{ mr: 1, fontSize: '16px' }} />
            Unselect
          </>
        ) : (
          'Select'
        )}
      </Box>
    );
  };

  const renderAccordion = (bundle: AncillariesBundleConfig, index: number) => {
    const disabled = !!isBundleDisabled(bundle, bundles);
    const included = !!isBundleIncluded(bundle);
    const isExpanded = expandedAccordion === index;

    return (
      <Accordion
        key={bundle.code}
        expanded={isExpanded}
        onChange={handleAccordionChange(index)}
        sx={{
          width: '100%',
          mb: 2,
          '&.Mui-expanded': { marginBottom: '20px' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            width: '100%',
            position: 'relative',
            pr: 6,
            '& .MuiAccordionSummary-content': {
              width: '100%',
              margin: 0,
              display: 'block',
            },
            '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
            '& .MuiAccordionSummary-expandIconWrapper': {
              position: 'absolute',
              right: 16,
              top: '12%',
              transform: 'translateY(-50%)',
            },
          }}
        >
          <Box sx={{ width: '100%', mt: 1.8 }}>
            <AbgTypography
              variant="h5"
              data-testid={`asci-bundle-title-${index}`}
            >
              {bundle.title}
            </AbgTypography>
            {isExpanded && bundle.asciBadge?.trim() && (
              <Chip
                label={bundle.asciBadge.trim()}
                size="small"
                sx={{
                  backgroundColor: (styleParts as any)?.cardStyle?.backgroundColor || '#91001D',
                  borderRadius: '2px',
                  mb: 1,
                  mt: 1,
                  maxWidth: 'fit-content',
                  '& .MuiChip-label': { textTransform: 'none !important' },
                }}
              />
            )}
            {isExpanded && (
              <AbgTypography
                variant={'bodySmall' as any}
                sx={{ color: '#736D6D' }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(bundle.description),
                }}
              />
            )}

            {isExpanded && bundle.items?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {bundle.items
                  .filter((item) => item.included)
                  .map((item) => (
                    <Box
                      key={item.code}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: '16px', color: '#2E7D32' }} />
                      <AbgTypography variant="bodySmallRegular">
                        {item.title}
                      </AbgTypography>
                    </Box>
                  ))}
                {bundle.items
                  .filter((item) => !item.included)
                  .map((item) => (
                    <Box
                      key={item.code}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '16px', color: '#D32F2F' }} />
                      <AbgTypography
                        variant="bodySmallRegular"
                        sx={{
                          color: '#736D6D',
                        }}
                      >
                        {item.title}
                      </AbgTypography>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>

          {!isExpanded && (
            <>
              {bundle.image?.src && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: '-30px',
                    ml: '35px',
                  }}
                >
                  <AemImage
                    src={bundle.image.src}
                    alt={bundle.image.alt}
                    width={80}
                    height={50}
                    style={{ objectFit: 'contain' }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 2,
                  pb: 1,
                  mr: -5,
                }}
              >
                {renderPrice(bundle)}
                {renderSelectButton(bundle, disabled, included)}
              </Box>
            </>
          )}
        </AccordionSummary>

        <AccordionDetails>
          {bundle.image?.src && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <AemImage
                src={bundle.image.src}
                alt={bundle.image.alt}
                width={250}
                height={120}
                style={{ objectFit: 'contain' }}
              />
            </Box>
          )}
          {pageType === AncillariesPageType.AddOns && (
            <AncillariesBundleItems
              items={bundle.items}
              styleParts={styleParts}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 1,
              backgroundColor: (styleParts as any)?.priceSection?.backgroundColor || '#F5F5F5',
              mx: -2,
              mb: -2.6,
              mt: 'auto',
            }}
          >
            {renderPrice(bundle)}
            {renderSelectButton(bundle, disabled, included)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderCard = (bundle: AncillariesBundleConfig, index: number) => {
    const disabled = !!isBundleDisabled(bundle, bundles);
    const included = !!isBundleIncluded(bundle);

    return (
      <Card
        key={bundle.code}
        sx={{
          p: { xs: 0, md: 2 },
          display: 'flex',
          flexDirection: 'column',
          border: selectedBundle === bundle.code ? '2px solid black' : 'none',
          overflow: 'visible',
        }}
      >
        <CardContent>
          <Box>
            <AbgTypography
              variant="h5"
              data-testid={`asci-bundle-title-${index}`}
            >
              {bundle.title}
            </AbgTypography>
            {bundle.asciBadge?.trim() && (
              <Chip
                label={bundle.asciBadge.trim()}
                color="primary"
                size="small"
                sx={{
                  backgroundColor: (styleParts as any)?.cardStyle?.backgroundColor || '#91001D',
                  borderRadius: '2px',
                  mb: 1,
                  mt: 1,
                  maxWidth: 'fit-content',
                  '& .MuiChip-label': { textTransform: 'none !important' },
                }}
              />
            )}
            <AbgTypography
              variant={'bodySmall' as any}
              sx={{ color: '#736D6D' }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(bundle.description),
              }}
            />

            {bundle.items?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {bundle.items
                  .filter((item) => item.included)
                  .map((item) => (
                    <Box
                      key={item.code}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: '16px', color: '#2E7D32' }} />
                      <AbgTypography variant="bodySmallRegular">
                        {item.title}
                      </AbgTypography>
                    </Box>
                  ))}
                {bundle.items
                  .filter((item) => !item.included)
                  .map((item) => (
                    <Box
                      key={item.code}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '16px', color: '#D32F2F' }} />
                      <AbgTypography
                        variant="bodySmallRegular"
                        sx={{
                          color: '#736D6D',
                        }}
                      >
                        {item.title}
                      </AbgTypography>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>

          {bundle.image?.src && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <AemImage
                src={bundle.image.src}
                alt={bundle.image.alt}
                width={300}
                height={150}
                style={{ objectFit: 'contain' }}
              />
            </Box>
          )}
          {pageType === AncillariesPageType.AddOns && (
            <AncillariesBundleItems
              items={bundle.items}
              styleParts={styleParts}
            />
          )}
        </CardContent>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: (styleParts as any)?.priceSection?.backgroundColor || '#F3EEEF',
            padding: '15px',
            mt: 'auto',
            mx: { xs: 0, md: -2 },
            mb: { xs: 0, md: -2 },
            borderRadius:
              selectedBundle === bundle.code ? '0 0 4px 4px' : undefined,
            overflow: 'visible',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {renderPrice(bundle)}
          {renderSelectButton(bundle, disabled, included)}
        </Box>
      </Card>
    );
  };

  return (
    <Box
      data-testid="asci-ancillaries-bundles-container"
      sx={{
        display: isSmallMobile ? 'block' : 'grid',
        gridTemplateColumns: isSmallMobile
          ? 'none'
          : `repeat(${Math.min(bundles.length, 3)}, 1fr)`,
        gap: 2,
        maxWidth: bundles.length === 1 ? '450px' : 'none',
        margin: '0 auto',
      }}
    >
      {bundles.map((bundle, index) =>
        isSmallMobile
          ? renderAccordion(bundle, index)
          : renderCard(bundle, index),
      )}
    </Box>
  );
};
